import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConnectionService } from './connection.service';
import { SquareTransaction } from '@contracts/interfaces/transaction.interfaces';

export interface SquareMoney { amount: number; currency: string; }
export interface SquareLocation { id: string; name: string; status: string; currency: string; }
export interface SquarePayment {
  id: string; created_at: string; status: string; location_id: string; order_id?: string;
  total_money?: SquareMoney; refunded_money?: SquareMoney;
  processing_fee: { amount_money: SquareMoney }[]; note?: string; reference_id?: string; source_type: string;
}
export interface SquareFilters { begin_time: string; end_time: string; location_id?: string; limit: number; }
export interface SquareTransfer {
  id: string; created_at: string; arrival_date?: string; status: string; location_id: string;
  type: string; amount_money?: SquareMoney; destination_type?: string; end_to_end_id?: string;
  payout_fee: { amount_money?: SquareMoney }[];
}
export type SquareEnvironment = 'sandbox' | 'production';
export interface SquarePayoutCandidate {
  id: string; locationId: string; amount: number; arrivalDate: string; status: string;
  reference: string; referenceMatch: 'full' | 'partial' | null;
}
export interface SquareRevenuePreview {
  ignoredTaxOnFeeNetTotal?: number;
  candidates: SquarePayoutCandidate[]; selected: SquarePayoutCandidate | null; squareTransactions: SquareTransaction[] | null;
}

@Injectable({ providedIn: 'root' })
export class SquareService {
  constructor(private http: HttpClient, private connection: ConnectionService) {}
  private options() { return { headers: { authToken: this.connection.activeConnection?.token || '' } }; }
  private endpoint(environment: SquareEnvironment) {
    return `${this.connection.address}/square-service${environment === 'sandbox' ? '/sandbox' : ''}`;
  }
  revenuePreview(sha1: string, payoutId?: string) {
    return this.http.post<SquareRevenuePreview>(`${this.endpoint('production')}/revenue-preview`, { sha1, ...(payoutId ? { payoutId } : {}) }, this.options());
  }
  status(environment: SquareEnvironment = 'production') {
    return this.http.get<{ configured: boolean; environment: string; apiVersion: string }>(
      `${this.endpoint(environment)}/status`, this.options());
  }
  locations(environment: SquareEnvironment = 'production') {
    return this.http.get<{ locations: SquareLocation[] }>(`${this.endpoint(environment)}/locations`, this.options());
  }
  payments(filters: SquareFilters, cursor?: string, environment: SquareEnvironment = 'production') {
    let params = new HttpParams();
    Object.entries({ ...filters, cursor }).forEach(([key, value]) => {
      if (value !== undefined && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<{ payments: SquarePayment[]; cursor: string | null }>(
      `${this.endpoint(environment)}/payments`, { ...this.options(), params });
  }
  transfers(filters: SquareFilters, cursor?: string, environment: SquareEnvironment = 'production') {
    let params = new HttpParams();
    Object.entries({ ...filters, cursor }).forEach(([key, value]) => {
      if (value !== undefined && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<{ transfers: SquareTransfer[]; cursor: string | null }>(
      `${this.endpoint(environment)}/transfers`, { ...this.options(), params });
  }
}
